import { useEffect, useState } from "react";

export interface StickyCtaOptions {
  /** Elements whose visibility should suppress the sticky CTA (CSS selectors). */
  selectors: string[];
  /** Don't show the bar until the user has scrolled this far. */
  minScroll?: number;
  /** Hide again within this distance of the document bottom. */
  footerGap?: number;
  /** Intersection threshold used for the collision observers. */
  threshold?: number;
  /** Viewport at (and below) which the sticky CTA is allowed at all. */
  mobileQuery?: string;
}

/**
 * Shared behaviour for the mobile sticky CTA pill used on the homepage and
 * /services: only on small viewports, only after the hero, never on top of
 * another visible call-to-action, and never overlapping the footer.
 */
export function useStickyCtaVisible({
  selectors,
  minScroll = 900,
  footerGap = 200,
  threshold = 0.2,
  mobileQuery = "(max-width: 760px)",
}: StickyCtaOptions) {
  const [isMobile, setIsMobile] = useState(false);
  const [scrolledPast, setScrolledPast] = useState(false);
  const [collision, setCollision] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia(mobileQuery);
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [mobileQuery]);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const max = document.body.scrollHeight - window.innerHeight;
      setScrolledPast(y >= minScroll && max - y > footerGap);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [minScroll, footerGap]);

  const key = selectors.join(",");
  useEffect(() => {
    if (!key) return;
    const els = Array.from(document.querySelectorAll(key));
    if (!els.length) return;
    const visible = new Set<Element>();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) visible.add(e.target);
          else visible.delete(e.target);
        });
        setCollision(visible.size > 0);
      },
      { threshold },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [key, threshold]);

  return { isMobile, visible: isMobile && scrolledPast && !collision, collision };
}

export default useStickyCtaVisible;